import type { ListingStoreState } from "src/stores/listing/listing.store";
import type {
  ListingFacet,
  ListingFacetKeys,
  ListingFacets,
} from "src/types/adapters/listing.type";
import {
  deleteSessionStorageItem,
  getSessionStorageJSON,
  setSessionStorageJSON,
} from "./helper/sessionStorage";

const STORAGE_KEY = "ot-listing-state";
const PDP_RESULT_ID_KEY = "ot-pdp-result-id";

export type PersistedListingState = {
  products: ListingStoreState["products"];
  page: ListingStoreState["page"];
  sort: ListingStoreState["sort"];
  sortOptions: ListingStoreState["sortOptions"];
  totalPages: ListingStoreState["totalPages"];
  totalResults: ListingStoreState["totalResults"];
  facets?: ListingFacets;
  tree?: ListingStoreState["tree"];
};

type SerializedListingState = Omit<PersistedListingState, "facets"> & {
  facets?: [ListingFacetKeys, ListingFacet][];
};

type StoredListingEntry = {
  id: string;
  state?: SerializedListingState;
  scrollY?: number;
};

type PdpResultIdEntry = {
  slug: string;
  resultId: string;
};

function getEntryIdFromPath(asPath: string): string {
  return Buffer.from(asPath).toString("base64");
}

function readEntry(): StoredListingEntry | null {
  return getSessionStorageJSON<StoredListingEntry>(STORAGE_KEY);
}

function writeEntry(entry: StoredListingEntry): void {
  setSessionStorageJSON<StoredListingEntry>(STORAGE_KEY, entry);
}

function serializeState(state: PersistedListingState): SerializedListingState {
  const { facets, ...rest } = state;

  return {
    ...rest,
    facets: facets ? Array.from(facets.entries()) : undefined,
  };
}

function deserializeState(
  state: SerializedListingState
): PersistedListingState {
  const { facets, ...rest } = state;

  return {
    ...rest,
    facets: facets ? new Map(facets) : undefined,
  };
}

export function loadListingState(asPath: string): PersistedListingState | null {
  const entry = readEntry();

  if (!entry || entry.id !== getEntryIdFromPath(asPath) || !entry.state) {
    return null;
  }

  return deserializeState(entry.state);
}

export function loadScrollPosition(asPath: string): number | null {
  const entry = readEntry();

  if (!entry || entry.id !== getEntryIdFromPath(asPath)) {
    return null;
  }

  return entry.scrollY ?? null;
}

export function saveListingState(
  asPath: string,
  state: PersistedListingState
): void {
  const id = getEntryIdFromPath(asPath);
  const existing = readEntry();
  const scrollY = existing?.id === id ? existing.scrollY : undefined;

  writeEntry({ id, state: serializeState(state), scrollY });
}

export function saveScrollPosition(asPath: string, scrollY: number): void {
  const id = getEntryIdFromPath(asPath);
  const existing = readEntry();
  const state = existing?.id === id ? existing.state : undefined;

  writeEntry({ id, state, scrollY });
}

export function clearListingState(): void {
  deleteSessionStorageItem(STORAGE_KEY);
}

export function savePdpResultId(slug: string, resultId: string): void {
  setSessionStorageJSON<PdpResultIdEntry>(PDP_RESULT_ID_KEY, {
    slug,
    resultId,
  });
}

export function getPdpResultId(slug: string): string | undefined {
  const entry = getSessionStorageJSON<PdpResultIdEntry>(PDP_RESULT_ID_KEY);

  return entry?.slug === slug ? entry.resultId : undefined;
}
